import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiDownload, ApiError } from '../api/client';
import { formatMkd, formatNumber, todayIso, monthAgoIso } from '../lib/format';
import { btn } from '../components/FilterBar';
import { useToast } from '../components/Toast';

/**
 * Raporti i fitimeve. Ports FitimetRaportet.
 *
 * The top panel is the desktop report, unchanged: nine named lines per gym,
 * each fed by one hardcoded article id, showing `numri` over the range. Those
 * are the numbers the client reads, so they are reproduced exactly rather than
 * corrected underneath them.
 *
 * Three of those lines are wrong at source, and the screen now says so instead
 * of leaving it to be discovered: article 16 does not exist so Stargym's "Plus
 * fitim" is always 0, article 18 is listed twice so two Missfit lines show the
 * same number, and any article missing from both lists — Giftcard, "Aktivizim i
 * karteles" — never appears. The full per-article table below covers everything
 * the fixed lines miss.
 */

interface ReportArticle {
  artikulli: string;
  units: number;
  amount: number;
}

interface ReportBusiness {
  slug: string;
  name: string;
  articles: ReportArticle[];
  totalUnits: number;
  totalAmount: number;
}

interface FixedLine {
  label: string;
  articleId: number;
  artikulli: string | null;
  units: number;
  duplicate: boolean;
}

interface FixedPanel {
  slug: string;
  name: string;
  lines: FixedLine[];
  totalUnits: number;
}

interface FixedReport {
  panels: FixedPanel[];
  uncovered: { slug: string; name: string; artikulli: string; units: number; amount: number }[];
}

interface ReportResponse {
  fixed: FixedReport;
  businesses: ReportBusiness[];
}

export default function FitimetRaportet() {
  const toast = useToast();
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);

  const reportQuery = useQuery({
    queryKey: ['fitimet-raportet', startDate, endDate],
    queryFn: () => apiGet<ReportResponse>('/fitimet/raportet', { startDate, endDate }),
    placeholderData: (prev) => prev,
  });

  async function handleExport() {
    try {
      await apiDownload('/fitimet/raportet/eksport', { startDate, endDate });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.');
    }
  }

  const businesses = reportQuery.data?.businesses ?? [];
  const fixed = reportQuery.data?.fixed;
  const panels = fixed?.panels ?? [];
  const uncovered = fixed?.uncovered ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Raporti i fitimeve</h1>
        <p className="text-sm text-muted">Njësitë e shitura dhe shuma për çdo artikull</p>
      </div>

      <div className="rounded-xl bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="rap-start">
              Prej
            </label>
            <input
              id="rap-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="rap-end">
              Deri
            </label>
            <input
              id="rap-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={businesses.length === 0}
            className={`ml-auto ${btn.secondary}`}
          >
            Eksporto Excel
          </button>
        </div>
      </div>

      {reportQuery.isLoading && (
        <div className="rounded-xl bg-white p-10 text-center text-muted">Duke ngarkuar…</div>
      )}

      {/* The desktop report, line for line. */}
      <div className="grid gap-5 lg:grid-cols-2">
        {panels.map((p) => (
          <section key={p.slug} className="overflow-hidden rounded-xl bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 className="font-semibold">{p.name}</h2>
              <p className="text-xs text-muted">Numri i njësive për periudhën e zgjedhur</p>
            </div>
            <ul className="divide-y divide-slate-100">
              {p.lines.map((line, i) => (
                <li
                  key={`${line.label}-${i}`}
                  className="flex items-baseline justify-between gap-3 px-4 py-2.5"
                >
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span>{line.label}:</span>
                    {line.artikulli === null && (
                      <span
                        className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800"
                        title={`Artikulli ${line.articleId} nuk ekziston në artikullatFitimet, prandaj kjo rreshtë është gjithmonë 0.`}
                      >
                        artikull i pavlefshëm
                      </span>
                    )}
                    {line.artikulli !== null && line.duplicate && (
                      <span
                        className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                        title={`Kjo rreshtë përdor të njëjtin artikull (${line.artikulli}) si një rreshtë tjetër më sipër.`}
                      >
                        dublikat: {line.artikulli}
                      </span>
                    )}
                  </span>
                  <span className="tnum font-medium">{formatNumber(line.units)}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {uncovered.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">
            Këta artikuj kanë fitime në këtë periudhë por nuk shfaqen në raportin më sipër:
          </p>
          <ul className="mt-2 space-y-1">
            {uncovered.map((u) => (
              <li key={`${u.slug}-${u.artikulli}`}>
                {u.name} — {u.artikulli}: {formatNumber(u.units)} njësi, {formatMkd(u.amount)}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs">
            Raporti i mësipërm lexon një listë fikse artikujsh, e trashëguar nga aplikacioni
            desktop. Tabela më poshtë i përfshin të gjithë.
          </p>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold">Të gjithë artikujt</h2>
        <p className="text-sm text-muted">
          Çdo artikull në bazën e të dhënave, me njësitë dhe shumën përkatëse
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {businesses.map((b) => (
          <section key={b.slug} className="overflow-hidden rounded-xl bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 className="font-semibold">{b.name}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[380px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-2.5 font-semibold">Artikulli</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Numri</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Shuma</th>
                  </tr>
                </thead>
                <tbody>
                  {b.articles.map((a) => (
                    <tr key={a.artikulli} className="border-b border-slate-100">
                      <td className="px-4 py-2">{a.artikulli}</td>
                      <td className="tnum px-4 py-2 text-right">{formatNumber(a.units)}</td>
                      <td className="tnum px-4 py-2 text-right">{formatMkd(a.amount)}</td>
                    </tr>
                  ))}
                  {b.articles.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-muted">
                        Nuk ka artikuj.
                      </td>
                    </tr>
                  )}
                  <tr className="bg-slate-100 font-semibold">
                    <td className="px-4 py-2.5">TOTAL</td>
                    <td className="tnum px-4 py-2.5 text-right">{formatNumber(b.totalUnits)}</td>
                    <td className="tnum px-4 py-2.5 text-right">{formatMkd(b.totalAmount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
